import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EventApi, EndeavorApi } from '../services/ApiService';
import { UpcomingAssignments } from '../components'; // via barrel
import EventDetails from '../components/events/EventDetails';
import { useData } from '../contexts/DataContext';
import { useToast } from '../contexts/ToastContext';
import './EventView.css';

function EventView() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { deleteEvent } = useData();
  const { addToast } = useToast();
  
  const [event, setEvent] = useState(null);
  const [endeavor, setEndeavor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch event and endeavor data
  const fetchEventData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('EventView: Fetching event data for ID:', eventId);

      // Fetch event details
      const eventResponse = await EventApi.getEvent(eventId);
      if (!eventResponse.ok) {
        throw new Error(`Failed to fetch event: ${eventResponse.status}`);
      }

      const eventData = await eventResponse.json();
      console.log('EventView: Event data:', eventData);
      setEvent(eventData);

      // Fetch endeavor data if event has endeavorId
      if (eventData.endeavorId) {
        try {
          const endeavorResponse = await EndeavorApi.getEndeavor(eventData.endeavorId);
          if (endeavorResponse.ok) {
            const endeavorData = await endeavorResponse.json();
            console.log('EventView: Endeavor data:', endeavorData);
            setEndeavor(endeavorData);
          }
        } catch (endeavorError) {
          console.warn('EventView: Error fetching endeavor:', endeavorError);
          // Don't fail the whole page if endeavor can't be loaded
        }
      }

    } catch (error) {
      console.error('EventView: Error fetching event data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchEventData();
    }
  }, [eventId, fetchEventData]);

  // Handle actions
  const handleEdit = useCallback(() => {
    console.log('Navigating to edit page for event:', eventId);
    // Clear any existing errors
    setError(null);
    navigate(`/event/${eventId}/edit`);
  }, [eventId, navigate]);

  const handleGoToEndeavor = useCallback(() => {
    if (!endeavor) {
      console.warn('No endeavor data available');
      return;
    }
    
    // Clear any existing errors
    setError(null);
    
    const endeavorId = endeavor.endeavorId || endeavor.id;
    if (endeavorId) {
      console.log('Navigating to endeavor:', endeavorId);
      navigate(`/course/${endeavorId}`);
    } else {
      console.error('No valid endeavor ID found');
    }
  }, [endeavor, navigate]);

  if (loading) {
    return (
      <div className="event-view-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading event...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-view-container">
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Event</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button 
              className="btn btn-secondary"
              onClick={() => navigate('/dashboard')}
            >
              Back to Dashboard
            </button>
            <button 
              className="btn btn-primary"
              onClick={fetchEventData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="event-view-container">
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h2>Event Not Found</h2>
          <p>The event you're looking for doesn't exist or has been deleted.</p>
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="event-view-container">
      <EventDetails
        event={event}
        endeavor={endeavor}
        onEdit={handleEdit}
        onGoToEndeavor={handleGoToEndeavor}
        onDelete={async (eventId) => {
          await deleteEvent(eventId);
          addToast('Event deleted successfully', 'success');
          navigate('/dashboard');
        }}
      />
      <div className="event-content">
        <UpcomingAssignments
          courseEndeavorId={event.endeavorId}
          courseName={endeavor?.name}
          eventId={eventId}
          defaultStatus="all"
          defaultDue="all"
        />
      </div>
    </div>
  );
}

export default EventView;

// NOTE: EventDetails now uses data-status & tokenized variables; page CSS updated to rely on global theme tokens.
